import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Cancel,
  Delete,
  Edit,
  RadioButtonChecked,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_MATCHING_CONFIGS,
  GET_MATCHING_RUNS,
  CREATE_MATCHING_CONFIG,
  UPDATE_MATCHING_CONFIG,
  DELETE_MATCHING_CONFIG,
  CREATE_MATCHING_RUN,
  LAUNCH_MATCHING_SURVEY,
  MatchingConfigsResult,
} from "../../Queries";
import { MatchingConfig, MatchingRun, MatchingRunStatus } from "../../types";
import { MatchingConfigEditor, DraftConfig } from "./MatchingConfigEditor";

const STEPS = ["Select Preferences", "Configure Run", "Review & Send"];

// Which run statuses mean the survey is already live (→ show settings view)
const LIVE_STATUSES = new Set([
  MatchingRunStatus.SurveyActive,
  MatchingRunStatus.Processing,
  MatchingRunStatus.Ready,
  MatchingRunStatus.Applied,
  MatchingRunStatus.LmsSynced,
]);

type SizeMode = "size" | "count";

interface RunDraft {
  configId: string;
  sizeMode: SizeMode;
  teamSize: number;
  numTeams: string;
  surveyDueDate: string;
  responseThreshold: number;
  reminderEnabled: boolean;
  notes: string;
}

function defaultRunDraft(configId = ""): RunDraft {
  return {
    configId,
    sizeMode: "size",
    teamSize: 4,
    numTeams: "",
    surveyDueDate: "",
    responseThreshold: 100,
    reminderEnabled: false,
    notes: "",
  };
}

interface Props {
  open: boolean;
  courseId: string;
  existingRun?: MatchingRun;
  onClose: () => void;
  onLaunched: (run: MatchingRun) => void;
  /** Called when instructor cancels (hard-deletes) the run from the settings overlay. */
  onCancelRun?: () => void;
}

export function MatchingWizard({
  open,
  courseId,
  existingRun,
  onClose,
  onLaunched,
  onCancelRun,
}: Props) {
  const [step, setStep] = useState(0);
  const [selectedConfigId, setSelectedConfigId] = useState<string>(
    existingRun?.configId ?? "",
  );
  const [runDraft, setRunDraft] = useState<RunDraft>(
    defaultRunDraft(existingRun?.configId ?? ""),
  );
  const [configEditorOpen, setConfigEditorOpen] = useState(false);
  // When editing an existing config, hold the config object here
  const [editingConfig, setEditingConfig] = useState<MatchingConfig | undefined>(undefined);
  const [launchError, setLaunchError] = useState("");

  // If the run is already live, show a settings view instead of the wizard
  const isSettingsView = !!existingRun && LIVE_STATUSES.has(existingRun.status);

  // Statuses where cancel is allowed (not yet applied to course groups)
  const isCancelableRun =
    existingRun &&
    new Set([
      MatchingRunStatus.SurveyActive,
      MatchingRunStatus.Processing,
      MatchingRunStatus.Ready,
    ]).has(existingRun.status);

  React.useEffect(() => {
    if (open) {
      setStep(0);
      setLaunchError("");
      setSelectedConfigId(existingRun?.configId ?? "");
      setRunDraft(defaultRunDraft(existingRun?.configId ?? ""));
    }
  }, [open, existingRun]);

  const { data: configsData, refetch: refetchConfigs } =
    useQuery<MatchingConfigsResult>(GET_MATCHING_CONFIGS, { skip: !open });
  const allConfigs = configsData?.matchingConfigs ?? [];

  // Only show template configs in the list; also show the just-selected non-template (current session)
  const visibleConfigs = allConfigs.filter(
    (c) => c.isTemplate || c.id === selectedConfigId,
  );

  const [createConfig, { loading: creatingConfig }] = useMutation(
    CREATE_MATCHING_CONFIG,
    { refetchQueries: ["GetMatchingConfigs"] },
  );

  const [updateConfig, { loading: updatingConfig }] = useMutation(
    UPDATE_MATCHING_CONFIG,
    { refetchQueries: ["GetMatchingConfigs"] },
  );

  const [deleteConfig] = useMutation(DELETE_MATCHING_CONFIG, {
    refetchQueries: ["GetMatchingConfigs"],
  });

  const [createRun, { loading: creatingRun }] = useMutation(
    CREATE_MATCHING_RUN,
    {
      refetchQueries: [{ query: GET_MATCHING_RUNS, variables: { courseId } }],
    },
  );

  const [launchSurvey, { loading: launching }] = useMutation(
    LAUNCH_MATCHING_SURVEY,
    {
      refetchQueries: [{ query: GET_MATCHING_RUNS, variables: { courseId } }],
    },
  );

  const selectedConfig = allConfigs.find((c) => c.id === selectedConfigId);

  const configInput = (draft: DraftConfig) => ({
    name: draft.name,
    description: draft.description,
    isTemplate: draft.isTemplate,
    timePreferenceWeight: 0.25,
    rolePreferenceWeight: 0.25,
    personalityWeight: 0.25,
    preferredTeammatesWeight: 0.25,
    similarityPreference: 0.0,
    questions: draft.questions.map((q, i) => ({
      questionType: q.questionType,
      label: q.label,
      description: q.description ?? "",
      weight: Math.max(1, 10 - i),
      order: i,
      options: q.options ?? [],
      required: true,
      similarityPreference: q.similarityPreference ?? null,
    })),
  });

  async function handleSaveConfig(draft: DraftConfig) {
    if (editingConfig) {
      // Update existing config
      await updateConfig({
        variables: { id: editingConfig.id, input: configInput(draft) },
      });
    } else {
      // Create new config
      const result = await createConfig({
        variables: { input: configInput(draft) },
      });
      const newConfig = result.data?.createMatchingConfig;
      if (newConfig?.id) {
        setSelectedConfigId(newConfig.id);
        setRunDraft((d) => ({ ...d, configId: newConfig.id }));
      }
    }
    setEditingConfig(undefined);
    setConfigEditorOpen(false);
  }

  async function handleDeleteConfig(configId: string, e: React.MouseEvent) {
    e.stopPropagation();
    await deleteConfig({ variables: { id: configId } });
    if (selectedConfigId === configId) {
      setSelectedConfigId("");
      setRunDraft((d) => ({ ...d, configId: "" }));
    }
    refetchConfigs();
  }

  async function handleLaunch() {
    setLaunchError("");
    try {
      const runResult = await createRun({
        variables: {
          input: {
            configId: selectedConfigId,
            // Always send a valid int — default to 4 if count mode
            teamSize: runDraft.sizeMode === "size" ? runDraft.teamSize : 4,
            numTeams:
              runDraft.sizeMode === "count" && runDraft.numTeams
                ? parseInt(runDraft.numTeams, 10)
                : null,
            surveyDueDate: runDraft.surveyDueDate || null,
            responseThreshold: runDraft.responseThreshold,
            reminderEnabled: runDraft.reminderEnabled,
            reminderFrequency: "",
            notes: runDraft.notes,
          },
        },
      });

      const run = runResult.data?.createMatchingRun;
      if (!run?.id) return;

      const launchResult = await launchSurvey({ variables: { runId: run.id } });
      const launchedRun = launchResult.data?.launchMatchingSurvey;
      if (launchedRun) {
        onLaunched(launchedRun);
        onClose();
      }
    } catch (err: unknown) {
      setLaunchError(
        err instanceof Error ? err.message : "An unexpected error occurred.",
      );
    }
  }

  const setField = <K extends keyof RunDraft>(key: K, value: RunDraft[K]) =>
    setRunDraft((d) => ({ ...d, [key]: value }));

  const canProceed = [
    selectedConfigId !== "",
    runDraft.sizeMode === "size"
      ? runDraft.teamSize >= 2
      : runDraft.numTeams !== "" && parseInt(runDraft.numTeams, 10) >= 1,
    true,
  ];

  // ── Settings view (run already live) ────────────────────────────────────────

  if (isSettingsView && existingRun) {
    const run = existingRun;
    const config = allConfigs.find((c) => c.id === run.configId);
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Matching Run Settings</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={0}>
            {[
              ["Configuration", config?.name ?? run.configId],
              [
                "Team structure",
                run.numTeams
                  ? `${run.numTeams} teams`
                  : `${run.teamSize} students per team`,
              ],
              [
                "Survey due",
                run.surveyDueDate
                  ? new Date(run.surveyDueDate).toLocaleString()
                  : "No deadline set",
              ],
              [
                "Auto-trigger threshold",
                `${run.responseThreshold ?? 80}% response rate`,
              ],
              ["Reminder emails", run.reminderEnabled ? "Enabled" : "Disabled"],
            ].map(([label, value]) => (
              <Box key={label as string} mb={1.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {label}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
            {run.notes && (
              <Box mb={1.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Message to students
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    fontStyle: "italic",
                  }}
                >
                  &ldquo;{run.notes}&rdquo;
                </Typography>
              </Box>
            )}
          </Stack>

          {process.env.NODE_ENV === "development" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                <strong>Dev note:</strong> Results may appear instantly here
                because Celery tasks run synchronously in development
                (CELERY_TASK_ALWAYS_EAGER=True). In production the engine runs
                asynchronously and typically takes 30–120 seconds.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {isCancelableRun && onCancelRun && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<Cancel />}
              aria-label="cancel matching"
              onClick={() => {
                onCancelRun();
                onClose();
              }}
            >
              Cancel Matching
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Wizard ───────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Team Matching</DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* ── Step 0 — Select Config ── */}
          {step === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Choose a saved matching preferences template or create a new
                one. Only templates appear here.
              </Typography>

              {visibleConfigs.length > 0 ? (
                <List
                  disablePadding
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  {visibleConfigs.map((c: MatchingConfig, idx) => (
                    <ListItem
                      key={c.id}
                      disablePadding
                      divider={idx < visibleConfigs.length - 1}
                      secondaryAction={
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit this configuration">
                            <IconButton
                              size="small"
                              aria-label={`edit config ${c.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingConfig(c);
                                setConfigEditorOpen(true);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete this configuration">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`delete config ${c.name}`}
                              onClick={(e) => handleDeleteConfig(c.id, e)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemButton
                        selected={selectedConfigId === c.id}
                        onClick={() => {
                          setSelectedConfigId(c.id);
                          setRunDraft((d) => ({ ...d, configId: c.id }));
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {selectedConfigId === c.id ? (
                            <RadioButtonChecked
                              color="primary"
                              fontSize="small"
                            />
                          ) : (
                            <RadioButtonUnchecked fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={c.name}
                          secondary={
                            c.description ||
                            `${c.questions.length} question${
                              c.questions.length !== 1 ? "s" : ""
                            }`
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" variant="body2" mb={2}>
                  No saved templates yet — create one below.
                </Typography>
              )}

              <Button
                variant="text"
                size="small"
                startIcon={<Add />}
                onClick={() => {
                  setEditingConfig(undefined);
                  setConfigEditorOpen(true);
                }}
              >
                New Matching Preferences
              </Button>
            </Box>
          )}

          {/* ── Step 1 — Run Config ── */}
          {step === 1 && (
            <Stack spacing={2.5}>
              {/* Team structure: toggle + compact input side-by-side */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Define team structure
                </Typography>
                {/* Center-align so the toggle text and input value sit at the
                    same visual height. The MUI floating label adds ~14 px of
                    overhead above the actual input box; without centering the
                    toggle buttons end up floating too high. */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                  mt={1}
                >
                  <ToggleButtonGroup
                    value={runDraft.sizeMode}
                    exclusive
                    onChange={(_e, v) => {
                      if (v) setField("sizeMode", v as SizeMode);
                    }}
                    size="small"
                  >
                    <ToggleButton value="size" sx={{ textTransform: "none" }}>
                      Team size
                    </ToggleButton>
                    <ToggleButton value="count" sx={{ textTransform: "none" }}>
                      # of teams
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {runDraft.sizeMode === "size" ? (
                    <TextField
                      label="Students per team"
                      type="number"
                      value={runDraft.teamSize}
                      onChange={(e) =>
                        setField("teamSize", parseInt(e.target.value, 10) || 4)
                      }
                      inputProps={{ min: 2, max: 20 }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  ) : (
                    <TextField
                      label="Number of teams"
                      type="number"
                      value={runDraft.numTeams}
                      onChange={(e) => setField("numTeams", e.target.value)}
                      inputProps={{ min: 1 }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Due date + threshold — plain flex row, no Grid negative margins */}
              <Box>
                <Box display="flex" gap={2} sx={{ flexWrap: "wrap" }}>
                  <TextField
                    label="Survey due date"
                    type="datetime-local"
                    size="small"
                    value={runDraft.surveyDueDate}
                    onChange={(e) => setField("surveyDueDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      minWidth: 180,
                      "& input": { colorScheme: "dark" },
                    }}
                  />
                  <TextField
                    label="Auto-trigger threshold (%)"
                    type="number"
                    size="small"
                    value={runDraft.responseThreshold}
                    onChange={(e) =>
                      setField(
                        "responseThreshold",
                        parseInt(e.target.value, 10) || 80,
                      )
                    }
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ flex: 1, minWidth: 160 }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Matching runs automatically when the threshold % of students
                  respond.
                </Typography>
              </Box>

              {/* Reminder emails */}
              <FormControlLabel
                control={
                  <Switch
                    checked={runDraft.reminderEnabled}
                    onChange={(e) =>
                      setField("reminderEnabled", e.target.checked)
                    }
                  />
                }
                label="Send reminder emails to non-responders"
              />

              {/* Notes */}
              <TextField
                label="Message to students (optional)"
                fullWidth
                multiline
                rows={2}
                value={runDraft.notes}
                onChange={(e) => setField("notes", e.target.value)}
                helperText="Shown in the survey email and at the top of the survey form."
              />
            </Stack>
          )}

          {/* ── Step 2 — Review ── */}
          {step === 2 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Review before sending
              </Typography>
              {[
                ["Configuration", selectedConfig?.name ?? selectedConfigId],
                [
                  "Team structure",
                  runDraft.sizeMode === "size"
                    ? `${runDraft.teamSize} students per team`
                    : `${runDraft.numTeams} teams`,
                ],
                ...(runDraft.surveyDueDate
                  ? [
                      [
                        "Survey closes",
                        new Date(runDraft.surveyDueDate).toLocaleString(),
                      ],
                    ]
                  : []),
                [
                  "Auto-trigger at",
                  `${runDraft.responseThreshold}% response rate`,
                ],
                [
                  "Reminders",
                  runDraft.reminderEnabled ? "Enabled" : "Disabled",
                ],
              ].map(([label, value]) => (
                <Box key={label as string} mb={1}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">{value as string}</Typography>
                </Box>
              ))}

              {runDraft.notes && (
                <Box mb={1}>
                  <Typography variant="caption" color="text.secondary">
                    Message to students
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 1,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      fontStyle: "italic",
                      mt: 0.25,
                    }}
                  >
                    &ldquo;{runDraft.notes}&rdquo;
                  </Typography>
                </Box>
              )}

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                Clicking <strong>Send Matching Survey</strong> will send a
                personalized email invitation to every student in this course.
                Each student receives a unique link to complete the survey.
              </Typography>

              {launchError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {launchError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          {step > 0 && (
            <Button onClick={() => setStep((s) => s - 1)}>Back</Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed[step]}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleLaunch}
              disabled={creatingRun || launching}
            >
              {creatingRun || launching ? "Sending…" : "Send Matching Survey"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <MatchingConfigEditor
        open={configEditorOpen}
        existing={editingConfig}
        onClose={() => {
          setEditingConfig(undefined);
          setConfigEditorOpen(false);
        }}
        onSave={handleSaveConfig}
        saving={creatingConfig || updatingConfig}
      />
    </>
  );
}
